import axios from 'axios';
import { fetch_form_structure } from '@/services/main_section_service';
import {
  CREATE_ONBOARDING_TEMPLATE_QUESTION_MAPPINGS_URL,
  GET_ONBOARDING_TEMPLATE_QUESTION_MAPPINGS_URL,
  get_request_config,
} from '@/utils/system_constants/constant';
import {
  extract_error_message,
  parse_response_data,
} from '@/utils/api_response_helpers';
import {
  build_existing_mappings,
  build_mapping_payload,
  flatten_form_structure,
} from './template_question_mapping_helpers';

const template_question_mapping_data_methods = {
  async reload_all() {
    this.reload_request_id += 1;
    const request_id = this.reload_request_id;

    const onboarding_form_id = Number(this.selected_onboarding_form_id) || null;
    const template_builder_form_id = Number(this.selected_template_builder_form_id) || null;

    if (!onboarding_form_id || !template_builder_form_id) {
      this.onboarding_questions = [];
      this.template_questions = [];
      this.template_question_mappings = {};
      return;
    }

    this.loading = true;

    try {
      const [onboarding_items, template_items] = await Promise.all([
        fetch_form_structure(onboarding_form_id),
        fetch_form_structure(template_builder_form_id),
      ]);

      if (request_id !== this.reload_request_id) return;

      this.onboarding_questions = flatten_form_structure(onboarding_items);
      this.template_questions = flatten_form_structure(template_items);
      await this.load_existing_mappings();
    } catch (error) {
      this.onboarding_questions = [];
      this.template_questions = [];
      this.template_question_mappings = {};
      this.show_error(
        extract_error_message(error, 'Failed to load template question mapping data.'),
      );
    } finally {
      this.loading = false;
    }
  },

  async load_existing_mappings() {
    const form_link_id = Number(this.selected_form_link_id) || null;
    if (!form_link_id) {
      this.template_question_mappings = {};
      return;
    }

    this.loading_existing_mappings = true;

    try {
      const response = await axios.get(
        GET_ONBOARDING_TEMPLATE_QUESTION_MAPPINGS_URL,
        get_request_config({
          params: { form_link_id },
        }),
      );

      const parsed = parse_response_data(response);
      this.template_question_mappings = build_existing_mappings(parsed?.data || []);
    } catch (error) {
      this.template_question_mappings = {};
      this.show_error(
        extract_error_message(error, 'Failed to load template question mappings.'),
      );
    } finally {
      this.loading_existing_mappings = false;
    }
  },

  async save_template_question_mappings() {
    if (this.saving) return;
    const onboarding_form_id = Number(this.selected_onboarding_form_id) || null;
    const template_form_id = Number(this.selected_template_form_id) || null;
    const mappings = build_mapping_payload(this.template_question_mappings);

    if (!onboarding_form_id || !template_form_id || !this.template_questions.length || !mappings.length) {
      this.show_error('Please complete the question mappings before saving.');
      return;
    }

    this.saving = true;
    const loader = await this.show_loader('Saving mappings...');

    try {
      const response = await axios.post(
        CREATE_ONBOARDING_TEMPLATE_QUESTION_MAPPINGS_URL,
        {
          onboarding_form_id,
          template_form_id,
          mappings,
        },
        get_request_config(),
      );
      const parsed = parse_response_data(response);

      if (parsed?.status === 'success') {
        this.show_success(parsed?.message || 'Question mappings saved successfully.');
        await this.load_existing_mappings();
        return;
      }

      this.show_error(parsed?.message || 'Failed to save question mappings.');
    } catch (error) {
      this.show_error(
        extract_error_message(error, 'Failed to save question mappings.'),
      );
    } finally {
      await this.close_loader(loader);
      this.saving = false;
    }
  },
};

export default template_question_mapping_data_methods;
